import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useHistory } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown } from 'primereact/dropdown';
import { useSelector } from 'react-redux';
import { withAccount } from '../../../components/AccountContext';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { growlShape } from '../../../components/prop-types';
import FileImportDropzone from '../../../components/FileImportDropzone';
import { Loading } from '../../../components/Loading';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_LANGUAGE_ACTION_TYPE = 'SET_LANGUAGE';

const orderAlphabetically = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

const i18nMessages = defineMessages({
  importSurveyCustomTranslations: {
    id: 'menu.item-bank-management.import-survey-custom-translations',
    defaultMessage: 'Import Survey Custom Translations',
  },
  downloadTemplate: {
    id: 'res_import.download_hint',
    defaultMessage: 'Download Import Template file',
  },
});

const ACTIVE_LOCALES_QUERY = gql`
  query($id: ID!) {
    activeLocales
  }
`;

const UPDATE_CUSTOM_SURVEY_TRANSLATIONS_MUTATION = gql`
  mutation updateCustomSurveyTranslationsMutation($input: UpdateCustomTranslationsInput!) {
    updateCustomSurveyTranslations(input: $input) {
      accountId
      language
    }
  }
`;

const UpdateSurveyCustomTranslations = ({ currentAccount, growl }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [updateSurveyCustomTranslations] = useMutation(UPDATE_CUSTOM_SURVEY_TRANSLATIONS_MUTATION);

  const {
    cldr: { localeDisplayName },
  } = useSelector(state => state.i18n);

  const initialState = {
    selectedLanguage: null,
    supportedLanguages: [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          selectedLanguage: action.payload.selectedLanguage,
          supportedLanguages: action.payload.supportedLanguages,
        };
      case SET_LANGUAGE_ACTION_TYPE:
        return {
          ...state,
          selectedLanguage: action.payload.selectedLanguage,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const { loading, error, data } = useQuery(ACTIVE_LOCALES_QUERY, { variables: { id: currentAccount } });

  const { activeLocales } = data;

  useEffect(() => {
    const dataIsAvailable = activeLocales && activeLocales.length > 0;

    if (dataIsAvailable) {
      const languages = activeLocales
        .map(lang => ({
          value: lang,
          label: localeDisplayName(lang),
        }))
        .sort(orderAlphabetically);

      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          selectedLanguage: languages[0].value,
          supportedLanguages: languages,
        },
      };

      dispatch(action);
    }
  }, [activeLocales, localeDisplayName]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const { selectedLanguage, supportedLanguages } = state;

  const onUpload = async (files) => {
    const file = files[0];
    try {
      await updateSurveyCustomTranslations({
        variables: {
          input: {
            accountId: currentAccount,
            // for using file drop component, the field name need to be mapped
            // to the input id, in this case, it's always 1
            fieldName: '1',
            language: selectedLanguage,
          },
          file,
        },
      }).then(() => growlActionSuccess(growl));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={formatMessage(i18nMessages.importSurveyCustomTranslations)}>
            <div className="p-grid">
              <div className="p-col-3">
                <FileImportDropzone
                  onUpload={onUpload}
                  onCancel={() => history.push(`/accounts/${currentAccount}/dashboard`)}
                />
              </div>
              <div className="p-col-3 import-custom-translations-dropdown-container">
                <Dropdown
                  className="dropdown"
                  filter
                  filterBy="label"
                  value={selectedLanguage}
                  options={supportedLanguages}
                  onChange={(event) => {
                    const action = {
                      type: SET_LANGUAGE_ACTION_TYPE,
                      payload: {
                        selectedLanguage: event.value,
                      },
                    };

                    dispatch(action);
                  }}
                />
              </div>
              <div className="p-col-12">
                <a target="_blank" href="/import-templates/import-survey-custom-translations.xlsx">
                  {formatMessage(i18nMessages.downloadTemplate)}
                </a>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

UpdateSurveyCustomTranslations.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(withGrowl(UpdateSurveyCustomTranslations));
